import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import { PackValidationContext, PackValidationContextProps } from "./pack-validation-context-props.ts";
import { putItem } from "@/queries/queries-helper.ts";
import ResponseApi from "@/models/interfaces/response-api.ts";
import ShellValidationDTO from "@/models/interfaces/dtos/shell-validation-dto.ts";
import ENDPOINTS from "@/services/endpoints.ts";
import { UpdateShellValidationDTO } from "@/models/interfaces/dtos/update-shell-validation-dto.ts";
import SpaceTypo from "@/models/enums/space-typo.ts";
import ThermicTypo from "@/models/enums/thermic-typo.ts";

const PackValidationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { ensureCanEdit } = useProjectEditAccess();

    const [modifiedShellTypes, setModifiedShellTypes] = useState<UpdateShellValidationDTO[]>([]);

    const sendValidations = useCallback(async () => {
        if (!ensureCanEdit()) {
            return false;
        }

        if (!modifiedShellTypes.length)
            return true;

        const toastId = toast.loading("Enregistrement en cours...", { autoClose: false, closeButton: false, closeOnClick: false });

        const response = await putItem<ResponseApi<ShellValidationDTO>>(ENDPOINTS.SHELL_VALIDATION_SET_NEW_TYPES, modifiedShellTypes);

        if (response == null) {
            toast.update(toastId, {
                render: "Une erreur réseau est survenue.",
                type: "error",
                isLoading: false,
                autoClose: false,
                closeButton: true
            });

            return false;
        }

        if (response.success) {
            toast.update(toastId, {
                render: "Les validations de coques ont correctement été changées.",
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });

            return true;
        }

        toast.update(toastId, {
            render: "Impossible de modifier les validations des coques.",
            type: "error",
            isLoading: false,
            autoClose: false,
            closeButton: true
        });

        return false;
    }, [ensureCanEdit, modifiedShellTypes]);

    const upsertShellModification = useCallback((stringValidationId: string, spaceType: SpaceTypo | undefined, thermalType: ThermicTypo | undefined) => {
        if (!ensureCanEdit()) {
            return;
        }

        let existingModification = modifiedShellTypes.find(m => m.ShellValidationId === stringValidationId);
        if (existingModification != null) {
            if (spaceType != null) existingModification.ShellType = spaceType;
            if (thermalType != null) existingModification.ThermalType = thermalType;

        } else {
            existingModification = { ShellValidationId: stringValidationId, ShellType: spaceType, ThermalType: thermalType };
        }

        setModifiedShellTypes(prev => [...prev.filter(m => m.ShellValidationId !== stringValidationId), existingModification]);
    }, [ensureCanEdit, modifiedShellTypes, setModifiedShellTypes]);

    const contextProps: PackValidationContextProps = {
        modifiedShellTypes,

        upsertShellModification,
        sendValidations
    }

    return (
        <PackValidationContext.Provider value={contextProps}>
            {children}
        </PackValidationContext.Provider>
    )
}

export default PackValidationProvider;